<template>
  <div class="el-tree" :class="{ 'el-tree--highlight-current': highlightCurrent }" role="tree">
    <virtual-list
      :height="height"
      :filter-nodes="filterNodes"
      :item-size="itemSize"
      :node-key="nodeKey"
      :is-reset-scroll-cache="isResetScrollCache"
      :is-empty="isEmpty"
      @reset="isResetScrollCache = false"
    />
    <div v-if="isEmpty" class="el-tree__empty-block">
      <span class="el-tree__empty-text">{{ emptyText }}</span>
    </div>
  </div>
</template>
<script>
import VirtualList from './virtual-list';
import TreeStore from './tree-store';

export default {
  name: 'ElTreeVirtualScroll',
  components: { VirtualList },
  props: {
    data: { type: Array, default: () => [] },
    props: { type: Object, default: () => ({ children: 'children', label: 'label', disabled: 'disabled' }) },
    expandOnClickNode: { type: Boolean, default: true },
    showCheckbox: { type: Boolean, default: false },
    defaultExpandAll: { type: Boolean, default: false },
    nodeKey: { type: String, default: 'id' },
    defaultExpandedKeys: { type: Array, default: () => [] },
    defaultCheckedKeys: { type: Array, default: () => [] },
    autoExpandParent: { type: Boolean, default: true },
    filterNodeMethod: Function,
    emptyText: { type: String, default: '暂无数据' },
    checkOnClickNode: { type: Boolean, default: false },
    checkStrictly: { type: Boolean, default: false },
    indent: { type: Number, default: 18 },
    highlightCurrent: { type: Boolean, default: false },
    currentNodeKey: [String, Number],
    renderContent: Function,
    renderAfterExpand: { type: Boolean, default: true },
    accordion: { type: Boolean, default: false },
    height: { type: [Number, String], default: 800 },
    itemSize: { type: Number, default: 26 },
  },
  data() {
    return {
      root: null,
      isResetScrollCache: false,
    };
  },
  computed: {
    isEmpty() {
      const { childNodes } = this.root;
      return !childNodes || childNodes.length === 0 || childNodes.every(({ visible }) => !visible);
    },
    filterNodes() {
      function traverse(children) {
        let ret = [];
        if (Array.isArray(children)) {
          children.forEach(child => {
            if (child.visible) {
              ret.push(child);
            }
            if (child.expanded) {
              ret = ret.concat(traverse(child.childNodes));
            }
          });
        }
        return ret;
      }
      return traverse(this.root?.childNodes || []);
    },
  },
  watch: {
    defaultExpandedKeys(newVal) {
      this.store.setDefaultExpandedKeys(newVal);
    },
    defaultCheckedKeys(newVal) {
      this.store.setDefaultCheckedKey(newVal);
    },
    checkStrictly(newVal) {
      this.store.checkStrictly = newVal;
    },
    data(newVal) {
      this.isResetScrollCache = true;
      this.store.setData(newVal);
    },
  },
  created() {
    this.store = new TreeStore({
      data: this.data,
      props: this.props,
      key: this.nodeKey,
      defaultExpandAll: this.defaultExpandAll,
      defaultExpandedKeys: this.defaultExpandedKeys,
      autoExpandParent: this.autoExpandParent,
      defaultCheckedKeys: this.defaultCheckedKeys,
      filterNodeMethod: this.filterNodeMethod,
      checkStrictly: this.checkStrictly,
      currentNodeKey: this.currentNodeKey,
    });
    this.root = this.store.root;
  },
  mounted() {},
  methods: {
    filter(value) {
      if (!this.filterNodeMethod) throw new Error('[El-Tree-Virtual-Scroll Warn] filterNodeMethod is required when filter');
      this.store.filter(value);
      this.isResetScrollCache = true;
    },
    getCheckedNodes(leafOnly, includeHalfChecked) {
      return this.store.getCheckedNodes(leafOnly, includeHalfChecked);
    },
    getCheckedKeys(leafOnly) {
      if (!this.nodeKey) throw new Error('[El-Tree-Virtual-Scroll Warn] nodeKey is required in getCheckedKeys');
      return this.store.getCheckedKeys(leafOnly);
    },
    getHalfCheckedNodes() {
      return this.store.getHalfCheckedNodes();
    },
    getHalfCheckedKeys() {
      if (!this.nodeKey) throw new Error('[El-Tree-Virtual-Scroll Warn] nodeKey is required in getHalfCheckedKeys');
      return this.store.getHalfCheckedKeys();
    },
    getCurrentNode() {
      const currentNode = this.store.getCurrentNode();
      return currentNode ? currentNode.data : null;
    },
    getCurrentKey() {
      if (!this.nodeKey) throw new Error('[El-Tree-Virtual-Scroll Warn] nodeKey is required in getCurrentKey');
      const currentNode = this.getCurrentNode();
      return currentNode ? currentNode[this.nodeKey] : null;
    },
    setCurrentNode(node) {
      if (!this.nodeKey) throw new Error('[El-Tree-Virtual-Scroll Warn] nodeKey is required in setCurrentNode');
      this.store.setUserCurrentNode(node);
    },
    setCurrentKey(key) {
      if (!this.nodeKey) throw new Error('[El-Tree-Virtual-Scroll Warn] nodeKey is required in setCurrentKey');
      this.store.setCurrentNodeKey(key);
    },
    getNode(data) {
      return this.store.getNode(data);
    },
    remove(data) {
      this.store.remove(data);
    },
    append(data, parentNode) {
      this.store.append(data, parentNode);
    },
    insertBefore(data, refNode) {
      this.store.insertBefore(data, refNode);
    },
    insertAfter(data, refNode) {
      this.store.insertAfter(data, refNode);
    },
    setCheckedKeys(keys, leafOnly) {
      if (!this.nodeKey) throw new Error('[El-Tree-Virtual-Scroll Warn] nodeKey is required in setCheckedKeys');
      this.store.setCheckedKeys(keys, leafOnly);
    },
    setCheckedNodes(nodes, leafOnly) {
      if (!this.nodeKey) throw new Error('[El-Tree-Virtual-Scroll Warn] nodeKey is required in setCheckedNodes');
      this.store.setCheckedNodes(nodes, leafOnly);
    },
    setChecked(data, checked) {
      this.store.setChecked(data, checked);
    },
    updateKeyChildren(key, data) {
      if (!this.nodeKey) throw new Error('[El-Tree-Virtual-Scroll Warn] nodeKey is required in updateKeyChild');
      this.store.updateChildren(key, data);
    },
    collapseAllNode() {
      function traverse(children) {
        if (Array.isArray(children)) {
          children.forEach(child => {
            child.expanded = false;
            traverse(child.childNodes || []);
          });
        }
      }
      traverse(this.root.childNodes);
    },
    expandAllNode() {
      function traverse(children) {
        if (Array.isArray(children)) {
          children.forEach(child => {
            child.expanded = true;
            traverse(child.childNodes || []);
          });
        }
      }
      traverse(this.root.childNodes);
    },
  },
};
</script>
